import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from '../utils/toast';
import { ArrowLeft, TrendingUp, TrendingDown, Award, Users, BarChart3, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AssessmentAnalyticsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/assessments/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading analytics...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!analytics) {
    return (
      <AdminLayout>
        <div className="p-8">
          <Card className="p-12 text-center">
            <p className="text-gray-500">No analytics data available</p>
          </Card>
        </div>
      </AdminLayout>
    );
  }

  const { summary, by_questionnaire, recent_attempts } = analytics;

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex items-center mb-6">
          <Button variant="outline" onClick={() => navigate('/assessments')} className="mr-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Assessment Analytics & Reports</h1>
            <p className="text-gray-600 mt-1">View performance metrics and insights across all assessments</p>
          </div>
        </div>

        {/* Summary Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <Card className="p-6 bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-100">Total Attempts</p>
                <p className="text-[28px] font-bold mt-1">{summary.total_attempts}</p>
              </div>
              <div className="bg-white/20 p-3 rounded-lg">
                <Users className="w-6 h-6" />
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-green-500 to-green-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-100">Passed</p>
                <p className="text-[28px] font-bold mt-1">{summary.passed_attempts}</p>
              </div>
              <div className="bg-white/20 p-3 rounded-lg">
                <CheckCircle className="w-6 h-6" />
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-red-500 to-red-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-100">Failed</p>
                <p className="text-[28px] font-bold mt-1">{summary.failed_attempts}</p>
              </div>
              <div className="bg-white/20 p-3 rounded-lg">
                <XCircle className="w-4 h-4" />
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-100">Pass Rate</p>
                <p className="text-[28px] font-bold mt-1">{summary.pass_rate}%</p>
              </div>
              <div className="bg-white/20 p-3 rounded-lg">
                <Award className="w-6 h-6" />
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-100">Avg Score</p>
                <p className="text-[28px] font-bold mt-1">{summary.average_score}%</p>
              </div>
              <div className="bg-white/20 p-3 rounded-lg">
                <BarChart3 className="w-6 h-6" />
              </div>
            </div>
          </Card>
        </div>

        {/* Performance by Questionnaire */}
        {by_questionnaire && by_questionnaire.length > 0 && (
          <Card className="p-6 mb-8">
            <h2 className="text-[18px] font-bold text-gray-900 mb-4">Performance by Assessment</h2>
            <div className="space-y-4">
              {by_questionnaire.map((q, index) => (
                <div key={q.questionnaire_id} className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{q.questionnaire_title}</h3>
                      <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                        <span>📝 {q.attempts} attempts</span>
                        <span className="text-green-600">✓ {q.passed} passed</span>
                        <span className="text-red-600">✗ {q.failed} failed</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center space-x-2">
                        <Badge className={`${
                          q.avg_score >= 80 ? 'bg-green-100 text-green-800' :
                          q.avg_score >= 60 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {q.avg_score}% avg
                        </Badge>
                        {q.avg_score >= 70 ? (
                          <TrendingUp className="w-5 h-5 text-green-600" />
                        ) : (
                          <TrendingDown className="w-5 h-5 text-red-600" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs text-gray-600">
                      <span>Pass Rate: {q.attempts > 0 ? Math.round((q.passed / q.attempts) * 100) : 0}%</span>
                      <span>{q.passed} / {q.attempts}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div 
                        className="bg-gradient-to-r from-green-500 to-green-600 h-full rounded-full transition-all duration-500"
                        style={{ width: `${q.attempts > 0 ? (q.passed / q.attempts) * 100 : 0}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Recent Attempts */}
        {recent_attempts && recent_attempts.length > 0 && (
          <Card className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-[18px] font-bold text-gray-900">Recent Attempts</h2>
              <Badge className="bg-gray-100 text-gray-800">Showing last {recent_attempts.length}</Badge>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-3 text-sm font-medium text-gray-700">Learner</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-700">Assessment</th>
                    <th className="text-center p-3 text-sm font-medium text-gray-700">Attempt #</th>
                    <th className="text-center p-3 text-sm font-medium text-gray-700">Score</th>
                    <th className="text-center p-3 text-sm font-medium text-gray-700">Result</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-700">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {recent_attempts.map((attempt, index) => (
                    <tr key={attempt.attempt_id} className="hover:bg-gray-50">
                      <td className="p-3">
                        <div>
                          <p className="font-medium text-gray-900">{attempt.learner_name}</p>
                          <p className="text-xs text-gray-600">{attempt.learner_email}</p>
                        </div>
                      </td>
                      <td className="p-3">
                        <p className="text-sm text-gray-900">{attempt.questionnaire_title}</p>
                      </td>
                      <td className="p-3 text-center">
                        <Badge className="bg-blue-100 text-blue-800">
                          #{attempt.attempt_number}
                        </Badge>
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex flex-col items-center">
                          <span className="font-bold text-gray-900">{attempt.score_percentage}%</span>
                          <span className="text-xs text-gray-600">
                            {attempt.points_earned}/{attempt.total_points} pts
                          </span>
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        {attempt.passed ? (
                          <Badge className="bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Passed
                          </Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800">
                            <XCircle className="w-4 h-4 mr-1" />
                            Failed
                          </Badge>
                        )}
                      </td>
                      <td className="p-3 text-sm text-gray-600">
                        {new Date(attempt.submitted_at).toLocaleDateString()} <br />
                        <span className="text-xs">{new Date(attempt.submitted_at).toLocaleTimeString()}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Empty State */}
        {summary.total_attempts === 0 && (
          <Card className="p-12 text-center">
            <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Data Yet</h3>
            <p className="text-gray-600 mb-6">
              Analytics will appear here once learners start taking assessments.
            </p>
            <div className="flex justify-center space-x-3">
              <Button onClick={() => navigate('/assessments/questions')} variant="outline">
                Create Questions
              </Button>
              <Button onClick={() => navigate('/assessments/create-questionnaire')} className="bg-blue-600">
                Create Assessment
              </Button>
            </div>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AssessmentAnalyticsPage;
